import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectUserObject } from '@common-reducers/UserSelectors';
import { DBUserThunks } from '@common/reducers/DBServiceThunks';
import { setUser } from '@common-reducers/UserReducer';
import type { UserInterface } from '@common-models/user';

type Preferences = NonNullable<UserInterface['preferences']>;

/**
 * Per-ACCOUNT UI state lives on the user document (`users.preferences`), never in a browser
 * flag — the same account on another device sees it, another account in this browser doesn't.
 * Patches the given keys over the current preferences and puts the saved user in the store.
 * Every key written here must be declared in the backend users schema (strict mode drops others).
 */
export function usePatchUserPreferences(): (next: (current: Preferences) => Partial<Preferences>) => Promise<void> {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserObject);
  return useCallback(async (next) => {
    if (!user?._id) return;
    const current = (user.preferences ?? {}) as Preferences;
    const res = await dispatch(DBUserThunks.patch({ entity: { _id: user._id, preferences: { ...current, ...next(current) } } as never }));
    const saved = (res as { payload?: UserInterface }).payload;
    if (saved) dispatch(setUser(saved));
  }, [dispatch, user]);
}
